import { FinnairItineraryItem } from '@fcom/dapi/api/models/finnair-itinerary-item';
import { FinnairItineraryItemFlight } from '@fcom/dapi/api/models/finnair-itinerary-item-flight';
import { FinnairItineraryItemType } from '@fcom/dapi/api/models/finnair-itinerary-item-type';
import { FinnairLocation } from '@fcom/dapi/api/models/finnair-location';
import { ItineraryItem } from '@fcom/dapi/api/models/itinerary-item';
import { ItineraryItemFlight } from '@fcom/dapi/api/models/itinerary-item-flight';
import { ItineraryItemLayover } from '@fcom/dapi/api/models/itinerary-item-layover';
import { isShortHaulFlight } from '@fcom/common/utils/flight.utils';
import { FinnairTechnicalStopsItem } from '@fcom/dapi/api/models/finnair-technical-stops-item';
import { FinnairTechnicalStopNote } from '@fcom/dapi/api/models/finnair-technical-stop-note';
import { FinnairBoundItem } from '@fcom/dapi/api/models/finnair-bound-item';

export const isFlight = (value: FinnairItineraryItem | ItineraryItem): value is FinnairItineraryItemFlight => {
  return value.type === FinnairItineraryItemType.FLIGHT;
};

export const isLongHaulLegAndAy = (
  itineraryArray: Array<ItineraryItemFlight | ItineraryItemLayover>,
  location: Record<string, FinnairLocation>
): boolean => {
  return itineraryArray
    .filter(isFlight)
    .filter((itinerary: FinnairItineraryItemFlight) => itinerary.operatingAirline.code === 'AY')
    .some((itinerary: FinnairItineraryItemFlight) => {
      const departureLocation = itinerary.departure.locationCode;
      const destinationLocation = itinerary.arrival.locationCode;

      const departureCountry = location[departureLocation].countryCode;
      const destinationCountry = location[destinationLocation].countryCode;

      return !isShortHaulFlight(
        { countryCode: departureCountry } as FinnairLocation,
        { countryCode: destinationCountry } as FinnairLocation
      );
    });
};

const supportedOffboardingTechnicalStopRoutes = [
  { departure: 'HEL', arrival: 'MEL' },
  { departure: 'MEL', arrival: 'HEL' },
];

const isOffboardingStop = (stop: FinnairTechnicalStopsItem): boolean => {
  return Boolean(stop.notes?.includes(FinnairTechnicalStopNote.OFFBOARDING_REQUIRED));
};

const isSupportedOffboardingTechnicalStopRoute = (flight: FinnairItineraryItemFlight): boolean => {
  return supportedOffboardingTechnicalStopRoutes.some(
    ({ departure, arrival }) =>
      departure === flight.departure.locationCode.toUpperCase() && arrival === flight.arrival.locationCode.toUpperCase()
  );
};

export const getFlightWithOffboardingTechnicalStop = (
  bound: FinnairBoundItem
): FinnairItineraryItemFlight | undefined => {
  return bound.itinerary
    .filter(isFlight)
    .filter(isSupportedOffboardingTechnicalStopRoute)
    .find((flight) => (flight.technicalStops ?? []).some(isOffboardingStop));
};

export enum TechnicalStopLeg {
  FIRST = 'FIRST',
  SECOND = 'SECOND',
}

export interface FlightLeg extends FinnairItineraryItemFlight {
  legKey: string;
  departureLocationCode: string;
  arrivalLocationCode: string;
  technicalStopLeg?: TechnicalStopLeg;
}

const getOffboardingTechnicalStop = (flight: FinnairItineraryItemFlight): FinnairTechnicalStopsItem | undefined => {
  if (!isSupportedOffboardingTechnicalStopRoute(flight)) {
    return undefined;
  }

  return (flight.technicalStops ?? []).find(isOffboardingStop);
};

export const mapFlightToTechnicalOffboardingLegsIfNecessary = (flight: FinnairItineraryItemFlight): FlightLeg[] => {
  const offboardingStop = getOffboardingTechnicalStop(flight);

  if (!offboardingStop) {
    return [
      {
        ...flight,
        legKey: flight.id,
        departureLocationCode: flight.departure.locationCode,
        arrivalLocationCode: flight.arrival.locationCode,
      },
    ];
  }

  return [
    {
      ...flight,
      legKey: `${flight.id}-${flight.departure.locationCode}${offboardingStop.arrival.locationCode}`,
      departureLocationCode: flight.departure.locationCode,
      arrivalLocationCode: offboardingStop.arrival.locationCode,
      technicalStopLeg: TechnicalStopLeg.FIRST,
    },
    {
      ...flight,
      legKey: `${flight.id}-${offboardingStop.departure.locationCode}${flight.arrival.locationCode}`,
      departureLocationCode: offboardingStop.departure.locationCode,
      arrivalLocationCode: flight.arrival.locationCode,
      technicalStopLeg: TechnicalStopLeg.SECOND,
    },
  ];
};
